
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthFlow } from '@/hooks/useAuthFlow';
import SignInForm from '@/components/auth/SignInForm';
import SignUpForm from '@/components/auth/SignUpForm';
import ForgotPasswordForm from '@/components/auth/ForgotPasswordForm';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [show2FA, setShow2FA] = useState(false);

  const {
    organizationCode,
    setOrganizationCode,
    email,
    setEmail,
    password,
    setPassword,
    fullName,
    setFullName,
    isLoading,
    userProfile,
    setUserProfile,
    currentOrganization,
    setCurrentOrganization,
    rememberOrganization,
    rememberEmail,
    handleRememberOrganization,
    handleRememberEmail,
    handleSignIn,
    handleSignUp,
    handlePasswordReset,
    setTwoFactorVerified,
    toast
  } = useAuthFlow();

  const resetForm = () => {
    setPassword('');
    setFullName('');
    setShowForgotPassword(false);
    setShow2FA(false);
    setUserProfile(null);
    setCurrentOrganization(null);
    // Don't reset email and organization code if they're being remembered
    if (!rememberEmail) {
      setEmail('');
    }
    if (!rememberOrganization) {
      setOrganizationCode('');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          {currentOrganization && (
            <div className="text-center mb-2">
              <div className="text-sm text-gray-600">
                {currentOrganization.name}
              </div>
            </div>
          )}
          <CardTitle className="text-2xl font-bold text-center">
            {showForgotPassword
              ? 'Reset Password'
              : show2FA
              ? 'Two-Factor Authentication'
              : isLogin
              ? 'Sign In'
              : 'Create Account'}
          </CardTitle>
          <CardDescription className="text-center">
            {showForgotPassword
              ? 'Enter your email to receive reset instructions'
              : show2FA
              ? 'Enter the 6-digit code from your authenticator app'
              : isLogin
              ? 'Welcome back! Please sign in to your account.'
              : 'Create a new account to get started.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {showForgotPassword ? (
            <ForgotPasswordForm
              email={email}
              setEmail={setEmail}
              isLoading={isLoading}
              rememberEmail={rememberEmail}
              handleRememberEmail={handleRememberEmail}
              onPasswordReset={handlePasswordReset}
              toast={toast}
            />
          ) : isLogin ? (
            <SignInForm
              organizationCode={organizationCode}
              setOrganizationCode={setOrganizationCode}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              isLoading={isLoading}
              show2FA={show2FA}
              setShow2FA={setShow2FA}
              userProfile={userProfile}
              currentOrganization={currentOrganization}
              rememberOrganization={rememberOrganization}
              handleRememberOrganization={handleRememberOrganization}
              rememberEmail={rememberEmail}
              handleRememberEmail={handleRememberEmail}
              onSignIn={handleSignIn}
              setTwoFactorVerified={setTwoFactorVerified}
              toast={toast}
            />
          ) : (
            <SignUpForm
              organizationCode={organizationCode}
              setOrganizationCode={setOrganizationCode}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              fullName={fullName}
              setFullName={setFullName}
              isLoading={isLoading}
              rememberOrganization={rememberOrganization}
              handleRememberOrganization={handleRememberOrganization}
              rememberEmail={rememberEmail}
              handleRememberEmail={handleRememberEmail}
              onSignUp={handleSignUp}
              toast={toast}
            />
          )}

          <div className="mt-4 space-y-2">
            {!showForgotPassword && !show2FA && (
              <>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    Forgot your password?
                  </button>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setIsLogin(!isLogin);
                      resetForm();
                    }}
                    className="text-sm text-gray-600 hover:text-gray-500"
                  >
                    {isLogin
                      ? "Don't have an account? Sign up"
                      : 'Already have an account? Sign in'}
                  </button>
                </div>
              </>
            )}

            {showForgotPassword && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    resetForm();
                  }}
                  className="text-sm text-gray-600 hover:text-gray-500"
                >
                  Back to sign in
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
